/**
 * TemplateSectionItem Component
 *
 * Individual section card in the template editor.
 * Displays section info with expand/collapse preview,
 * drag handle for reordering using @dnd-kit, and remove action.
 */

import React, { useState } from 'react';
import Logo from '@components/Icons/Logo';
import { NoteSectionDisplayBox } from '@components/Note/NoteSectionDisplayBox';
import { useThemeContext } from '@context/theme/context';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Close as CloseIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getUserInfoFromLocalStorage } from '@utils/LocalStorageUtils';

import type { SectionWithMeta } from '../types';

import { DragHandle } from './primitives/DragHandle';

interface TemplateSectionItemProps {
  /** Section data with metadata */
  section: SectionWithMeta;
  /** Whether section can be edited */
  canModify: boolean;
  /** Callback to remove section */
  onRemove?: () => void;
  /** Callback to revert customization */
  onRevertCustomization?: () => void;
}

export const TemplateSectionItem: React.FC<TemplateSectionItemProps> = ({
  section,
  canModify,
  onRemove,
  onRevertCustomization,
}) => {
  const theme = useTheme();
  const { brandLanguage } = useThemeContext();
  const [showPreview, setShowPreview] = useState(false);
  const userInfo = getUserInfoFromLocalStorage();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.module_id,
    animateLayoutChanges: () => false, // Disable layout animations for better performance
    disabled: !canModify, // Disable sorting when can't modify
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  const handlePreviewToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPreview(!showPreview);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const handleRevert = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRevertCustomization?.();
  };

  // Get example content for preview
  const exampleContent =
    section.moduleInfo?.example ||
    section.moduleInfo?.public_example ||
    'No example available.';

  // Get description
  const description =
    section.moduleInfo?.description || section.moduleInfo?.public_description;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        ...(isDragging && {
          '& .MuiCard-root': {
            boxShadow: theme.shadows[8],
            borderColor: 'primary.main',
          },
        }),
      }}
    >
      <Card
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            borderColor: canModify ? 'primary.light' : 'divider',
          },
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            {/* Drag Handle (only if can modify) */}
            {canModify && (
              <Box
                sx={{
                  mt: 0.5,
                  ml: -1,
                }}
              >
                <DragHandle
                  listeners={listeners}
                  attributes={attributes}
                  size="small"
                />
              </Box>
            )}

            {/* Section Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.primary"
                sx={{ mb: 0.5 }}
              >
                {section.display_name}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {/* Preview Toggle */}
              <Tooltip
                title={showPreview ? 'Hide preview' : 'Show preview'}
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={handlePreviewToggle}
                  sx={{ color: 'text.secondary' }}
                >
                  {showPreview ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              {/* Customization Indicator */}
              {section.isCustomized && (
                <Tooltip title="This section has been customized" placement="top">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      '&:hover .revert-btn': {
                        display: 'flex',
                      },
                    }}
                  >
                    <StarIcon fontSize="small" color="primary" />
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontSize: '0.625rem' }}
                    >
                      Customized
                    </Typography>
                    {onRevertCustomization && (
                      <Button
                        className="revert-btn"
                        variant="text"
                        size="small"
                        onClick={handleRevert}
                        sx={{
                          display: 'none',
                          fontSize: '0.625rem',
                          minWidth: 'auto',
                          p: 0,
                        }}
                      >
                        Revert?
                      </Button>
                    )}
                  </Box>
                </Tooltip>
              )}

              {/* Section Origin Indicator */}
              {section.moduleInfo?.is_custom_user_module ? (
                <Tooltip title="Your custom section" placement="top">
                  <Avatar
                    src={userInfo?.img}
                    alt="User"
                    sx={{ width: 24, height: 24 }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title={`${brandLanguage.brandName} section`}
                  placement="top"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Logo size={20} />
                  </Box>
                </Tooltip>
              )}

              {/* Remove Button (only if can modify) */}
              {canModify && onRemove && (
                <IconButton
                  size="small"
                  onClick={handleRemove}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Preview Content */}
          <Collapse in={showPreview}>
            <Box sx={{ mt: 2 }}>
              <NoteSectionDisplayBox>{exampleContent}</NoteSectionDisplayBox>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};
